import * as React from "react";
import { useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { Arrows } from "src/assets/icons/arrows";
import { BulletList } from "src/assets/icons/bulletList";
import { Button } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";
import { withShowToggle } from "src/components/hoc/withShowToggle";
import { DataProviderContext } from "src/core/provider/dataProvider";
import { CandidateMode } from "src/utils/api/configD";
import { getFabricRoute } from "src/utils/routesUtils";

import { usePreviewChangesDrawer } from "./usePreviewChangesDrawer";

import "./candidateActions.scss";

interface CandidateActionsProps {
  readonly mode: CandidateMode;
  readonly setMode: (m: CandidateMode) => void;
  readonly onPreviewChanges?: () => void;
  readonly onReviewConfiguration?: () => void;
  readonly previewChangesDrawer?: React.ReactElement | null;
}

interface ButtonProps {
  handleAction: () => void;
}

const SwitchToEdit = withShowToggle(({ handleAction }: ButtonProps) => {
  return (
    <Button name="switch-to-edit" kind="tertiary" onClick={handleAction}>
      <Arrows /> Switch to edit mode
    </Button>
  );
});

const ViewRunning = withShowToggle(({ handleAction }: ButtonProps) => {
  return (
    <Button name="view-running" kind="tertiary" onClick={handleAction}>
      <Arrows /> View running mode
    </Button>
  );
});

const PreviewChanges = withShowToggle(({ handleAction }: ButtonProps) => {
  return (
    <Button name="preview-changes" kind="secondary" onClick={handleAction}>
      <BulletList />
      Preview changes
    </Button>
  );
});

const ReviewConfiguration = withShowToggle(({ handleAction }: ButtonProps) => {
  return (
    <Button name="review-configuration" kind="primary" onClick={handleAction}>
      Review configuration
    </Button>
  );
});

/**
 * Generic candidate actions component that can be used for any candidate type
 */
export const CandidateActions = ({
  mode,
  setMode,
  onPreviewChanges,
  onReviewConfiguration,
  previewChangesDrawer
}: CandidateActionsProps) => {
  return (
    <HFlex gap={4} className="action-section" align="center" justify="start">
      <SwitchToEdit
        show={mode === CandidateMode.Running}
        handleAction={() => setMode(CandidateMode.Edit)}
      />
      <ViewRunning
        show={mode === CandidateMode.Edit}
        handleAction={() => setMode(CandidateMode.Running)}
      />
      {onPreviewChanges && (
        <PreviewChanges
          show={mode === CandidateMode.Edit}
          handleAction={onPreviewChanges}
        />
      )}
      {onReviewConfiguration && (
        <ReviewConfiguration
          show={mode === CandidateMode.Edit}
          handleAction={onReviewConfiguration}
        />
      )}
      {previewChangesDrawer}
    </HFlex>
  );
};

/**
 * Fabric-specific candidate actions component (backward compatibility wrapper)
 */
export const FabricCandidateActions = () => {
  const { previewChangesDrawer, openPreviewChangesDrawer } =
    usePreviewChangesDrawer();
  const { setMode, mode, fabric } = useContext(DataProviderContext);

  const redirect = useNavigate();

  const reviewConfiguration = useCallback(() => {
    if (fabric) {
      redirect(getFabricRoute(fabric.id, "candidate"));
    }
  }, [redirect, fabric]);

  return (
    <CandidateActions
      mode={mode}
      setMode={setMode}
      onPreviewChanges={openPreviewChangesDrawer}
      onReviewConfiguration={reviewConfiguration}
      previewChangesDrawer={previewChangesDrawer}
    />
  );
};
